import { Dropdown, Input, TableRow } from '@sam/components';
import { NavigateFunction } from 'react-router-dom';
import {
  createLanguageMap,
  Customer,
  DeliveryNote,
  DeliveryNoteCondition,
  DeliveryType,
  generateAddressString,
  generateDropdownOptions,
  Language,
  Order,
  Project,
  Receipt,
  Task,
  UnitCode,
} from 'shared';
import { CustomerLocation } from 'shared/src/customerLocation/CustomerLocation.types';
import { uid } from 'uid';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import i18n from '../../i18n/i18n';
import { generateEmptyTask } from '../task/task.utils';
import { sortByNumberRangeNumber } from '../general.utils';

/**
 * Util mehthod to create tableRows with offered tasks to be edited
 * @param offeredTasks array of all positions
 * @param tasks all available tasks
 * @param onChange update mthod
 * @param setOfferedTaskIdToDelete sets the id of a position to delete it
 * @returns array of tableRows
 */
export const convertTasksIntoDeliveryNoteTableEntries = (
  deliveryNote: DeliveryNote,
  tasks: Task[],
  onChange: (deliveryNote: DeliveryNote) => void,
  deletePosition: (id: string) => void,
  language: Language,
): TableRow[] =>
  deliveryNote.deliveredPositions.map((position) => ({
    id: position.id,
    content: [
      position.position,
      <Dropdown
        create
        clearable
        onCreateNew={(newEntry) =>
          onChange({
            ...deliveryNote,
            deliveredPositions: deliveryNote.deliveredPositions.map((deliveredPosition) => {
              return deliveredPosition.id === position.id
                ? {
                    ...deliveredPosition,
                    task: generateEmptyTask({
                      title: newEntry.value,
                      unitCode: UnitCode.NUMBER_OF_PARTS,
                      id: uid(),
                      translations: createLanguageMap(newEntry.value),
                    }),
                  }
                : deliveredPosition;
            }),
          })
        }
        options={
          tasks.some((task) => task.id === position.task.id || !position.task.id)
            ? generateDropdownOptions(tasks, ['identifier', 'title'], 'id')
            : [
                ...generateDropdownOptions(tasks, ['identifier', 'title'], 'id'),
                {
                  label: position.task.title,
                  value: position.task.id,
                },
              ]
        }
        selectedOption={position.task.id}
        onChange={(taskId) => {
          onChange({
            ...deliveryNote,
            deliveredPositions: deliveryNote.deliveredPositions.map((deliveredPosition) =>
              deliveredPosition.id === position.id
                ? {
                    ...deliveredPosition,
                    task:
                      tasks.find((task) => task.id === taskId) ||
                      generateEmptyTask({
                        identifier: taskId,
                        title: taskId,
                      }),
                  }
                : deliveredPosition,
            ),
          });
        }}
      />,

      <Input
        type="text"
        value={position.task?.translations?.get(language)}
        onChange={(description) => {
          onChange({
            ...deliveryNote,
            deliveredPositions: deliveryNote.deliveredPositions.map((singlePosition) =>
              singlePosition.id === position.id
                ? {
                    ...singlePosition,
                    task: {
                      ...singlePosition.task,
                      translations: createLanguageMap(description),
                    },
                  }
                : singlePosition,
            ),
          });
        }}
      />,
      <Input
        type="text"
        value={position.batchNumber}
        onChange={(batchNumber) => {
          onChange({
            ...deliveryNote,
            deliveredPositions: deliveryNote.deliveredPositions.map((singlePosition) =>
              singlePosition.id === position.id
                ? {
                    ...singlePosition,
                    batchNumber,
                  }
                : singlePosition,
            ),
          });
        }}
      />,
      <Input
        value={position.comment}
        type="text"
        onChange={(comment) => {
          onChange({
            ...deliveryNote,
            deliveredPositions: deliveryNote.deliveredPositions.map((task) =>
              task.id === position.id
                ? {
                    ...task,
                    comment,
                  }
                : task,
            ),
          });
        }}
      />,
      <Dropdown
        selectedOption={position.condition}
        options={Object.values(DeliveryNoteCondition).map((condition) => ({
          label: i18n.t(`general.condition.${condition}`),
          value: condition,
        }))}
        onChange={(condition) => {
          onChange({
            ...deliveryNote,
            deliveredPositions: deliveryNote.deliveredPositions.map((task) =>
              task.id === position.id
                ? {
                    ...task,
                    condition: condition as DeliveryNoteCondition,
                  }
                : task,
            ),
          });
        }}
      />,
      <Input
        value={position.amount}
        type="number"
        onChangeNumber={(amount) => {
          onChange({
            ...deliveryNote,
            deliveredPositions: deliveryNote.deliveredPositions.map((task) =>
              task.id === position.id
                ? {
                    ...task,
                    amount,
                  }
                : task,
            ),
          });
        }}
      />,
      <Input value={i18n.t(`general.unitCode.${position.unitCode}`)} type="text" disabled />,
      <Dropdown
        options={Object.values(DeliveryType).map((type) => ({
          label: i18n.t(`pages.deliveryNote.create.${type}`),
          value: type,
        }))}
        selectedOption={position.deliveryType}
        onChange={(deliveryType) =>
          onChange({
            ...deliveryNote,
            deliveredPositions: deliveryNote.deliveredPositions.map((deliveredPosition) =>
              deliveredPosition.id === position.id
                ? {
                    ...deliveredPosition,
                    deliveryType: deliveryType as DeliveryType,
                  }
                : deliveredPosition,
            ),
          })
        }
      />,
      <IconComponent
        type={IconType.DELETE}
        width={30}
        color="#BC2E46"
        onClick={() => deletePosition(position.id)}
      />,
    ],
  }));

/**
 * Util method to convert deliveryNotes into TableRows
 * @param deliveryNotes the actual entries to display
 * @param orders list of orders to get customerOrderNumber to display
 * @param projects  list of projects to get the project numberRangeNumber for the deliveryNote
 * @param receipts receipt to display the numberRangenumber, if assigned
 * @returns Array of TableRows
 */
export const convertDeliveryNotesIntoTableEntries = (
  deliveryNotes: DeliveryNote[],
  orders: Order[],
  projects: Project[],
  receipts: Receipt[],
  customerLocations: CustomerLocation[],
  customers: Customer[],
  navigate: NavigateFunction,
  setNoteToDelete: (id: string) => void,
): TableRow[] => {
  return sortByNumberRangeNumber(deliveryNotes, (note) => note.numberRangeNumber).map((note) => {
    const order: Order | undefined = orders.find((order) => order.id === note.orderId);
    const project: Project | undefined = projects.find((project) => project.id === note.projectId);
    const receipt: Receipt | undefined = receipts.find((receipt) => receipt.id === note.invoiceId);
    const customerLocation: CustomerLocation | undefined = customerLocations.find((location) =>
      note.customConfig
        ? location.id === note.customConfig?.customerLocationId
        : location.id === order?.locationId,
    );
    const customer: Customer | undefined = customers.find((customer) =>
      note.customConfig?.customerId
        ? customer.id === note.customConfig?.customerId
        : customer.id === order?.customerId,
    );

    const orderNumbers: string[] = [];
    if (note.customerOrderNumber && note.customerOrderNumber !== '')
      orderNumbers.push(note.customerOrderNumber);
    if (order?.customerOrderNumber && order.customerOrderNumber !== '')
      orderNumbers.push(order.customerOrderNumber);
    note.deliveredPositions
      .filter((position) => !!position.batchNumber)
      .forEach((position) => orderNumbers.push(position.batchNumber));
    const customerOrderNumber: string = orderNumbers.join(', ');

    const deliveryLocation: string = note.deliveryAddress
      ? generateAddressString(note.deliveryAddress)
      : generateAddressString(customerLocation?.contact, customerLocation?.name);

    return {
      id: note.id,
      onClick: () => navigate('/delivery/edit', { state: { note } }),
      content: [
        note.numberRangeNumber,
        customerOrderNumber,
        project?.numberRangeNumber || '-',
        note.deliveryDate.toLocaleDateString('DE-de'),
        customer?.name || '-',
        deliveryLocation,
        note.delivered
          ? i18n.t('pages.deliveryNote.overview.delivered')
          : i18n.t('pages.deliveryNote.overview.notDelivered'),
        note.invoiceId && receipt
          ? receipt.invoiceNumber
          : i18n.t('pages.deliveryNote.overview.notAssigned'),
      ],
      actions: [
        <IconComponent
          type={IconType.EDIT}
          className="clickable"
          width={30}
          onClick={() => navigate('/delivery/edit', { state: { note } })}
        />,
        <IconComponent
          type={IconType.DELETE}
          className="delete"
          width={30}
          onClick={(evt) => {
            evt.stopPropagation();
            setNoteToDelete(note.id);
          }}
        />,
      ],
    };
  });
};
